import React, { useEffect, useState } from 'react';
import { API_BASE_URL, apiClient, handleApiError } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

const FinanceDashboard: React.FC = () => {
  const [summary, setSummary] = useState<{ revenue: number; sales: number; tokens_sold: number; avg_ticket: number } | null>(null);
  const [consults, setConsults] = useState<{ count: number; last_7d: number; last_30d: number; paid: number } | null>(null);
  const [status, setStatus] = useState<'pending'|'approved'|'rejected'|'all'>('approved');
  const [items, setItems] = useState<any[]>([]);
  const [theme, setTheme] = useState<any | null>(null);
  const [tokenItems, setTokenItems] = useState<{ amount?: number; price?: string; description?: string }[]>([{},{},{}]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>("");

  const token = localStorage.getItem('token');
  const { refreshTheme } = useTheme();

  const loadSummary = async () => {
    try {
      const r = await apiClient.get(`/admin/finance/summary`);
      setSummary(r.data);
    } catch {}
  };
  const loadConsultations = async () => {
    try {
      const [all, paid] = await Promise.all([
        apiClient.get(`/admin/finance/consultations`),
        apiClient.get(`/admin/finance/consultations?paid=true`),
      ]);
      setConsults({ count: all.data.count, last_7d: all.data.last_7d, last_30d: all.data.last_30d, paid: paid.data.count });
    } catch {}
  };
  const loadTransactions = async () => {
    try {
      const qs = status==='all' ? '' : `?status=${status}`;
      const r = await apiClient.get(`/admin/finance/transactions${qs}`);
      setItems(r.data.items || []);
    } catch {}
  };
  const loadActiveTheme = async () => {
    try {
      const r = await apiClient.get(`/theme/active`);
      const t = r.data;
      setTheme(t);
      const its = t?.tokens?.items || [];
      const arr = [its[0] || {}, its[1] || {}, its[2] || {}];
      setTokenItems(arr);
    } catch {}
  };
  const saveTokenPackages = async () => {
    if (!token) { setMessage('Token não encontrado'); return; }
    setSaving(true);
    try {
      const themeData = {
        name: theme?.name || 'Tema Personalizado',
        colors: theme?.colors,
        typography: theme?.typography,
        layout: theme?.layout,
        images: theme?.images,
        ctaColors: theme?.ctaColors,
        customCss: theme?.customCss || '',
        features: theme?.features,
        selection: theme?.selection,
        tokens: { ...(theme?.tokens || {}), items: tokenItems }
      };

      if (theme?.id && theme.id !== 0) {
        const r = await apiClient.put(`/theme/configs/${theme.id}`, themeData);
        const updated = r.data;
        setTheme(updated);
        await refreshTheme();
        setMessage('Pacotes de tokens atualizados');
      } else {
        const r = await apiClient.post(`/theme/configs`, themeData);
        const created = r.data;
        await apiClient.post(`/theme/configs/${created.id}/activate`);
        setTheme(created);
        await refreshTheme();
        setMessage('Pacotes de tokens salvos e tema ativado');
      }
      setTimeout(() => setMessage(''), 3000);
    } catch (e: any) {
      setMessage(handleApiError(e));
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { loadSummary(); loadConsultations(); }, []);
  useEffect(() => { loadTransactions(); }, [status]);
  useEffect(() => { loadActiveTheme(); }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Faturamento</div>
          <div className="text-white text-2xl">R$ {(summary?.revenue || 0).toFixed(2)}</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Vendas</div>
          <div className="text-white text-2xl">{summary?.sales || 0}</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Tokens vendidos</div>
          <div className="text-white text-2xl">{summary?.tokens_sold || 0}</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Ticket médio</div>
          <div className="text-white text-2xl">R$ {(summary?.avg_ticket || 0).toFixed(2)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Consultas (total)</div>
          <div className="text-white text-2xl">{consults?.count ?? 0}</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Últimos 7 dias</div>
          <div className="text-white text-2xl">{consults?.last_7d ?? 0}</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Últimos 30 dias</div>
          <div className="text-white text-2xl">{consults?.last_30d ?? 0}</div>
        </div>
        <div className="bg-white/10 p-4 rounded-xl border border-white/20">
          <div className="text-purple-300">Pagas com token</div>
          <div className="text-white text-2xl">{consults?.paid ?? 0}</div>
        </div>
      </div>

      <div className="bg-white/10 p-4 rounded-xl border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-lg">Transações</h3>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="bg-white/10 text-white px-3 py-2 rounded border border-white/20">
            <option value="all">Todas</option>
            <option value="approved">Aprovadas</option>
            <option value="pending">Pendentes</option>
            <option value="rejected">Rejeitadas</option>
          </select>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm text-purple-100">
            <thead>
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Usuário</th>
                <th className="text-left p-2">Valor</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Método</th>
                <th className="text-left p-2">Data</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-white/10">
                  <td className="p-2">{it.id}</td>
                  <td className="p-2">{it.user_id}</td>
                  <td className="p-2">R$ {Number(it.amount || 0).toFixed(2)}</td>
                  <td className="p-2">{it.status}</td>
                  <td className="p-2">{it.method}</td>
                  <td className="p-2">{it.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white/10 p-4 rounded-xl border border-white/20">
        <h3 className="text-white text-lg mb-4">Pacotes de Tokens</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {tokenItems.map((it, idx) => (
            <div key={idx} className="bg-white/5 p-4 rounded-xl border border-white/10">
              <label className="block text-sm text-purple-200 mb-1">Tokens</label>
              <input type="number" value={it.amount ?? ''} onChange={(e) => {
                const next = [...tokenItems];
                next[idx] = { ...next[idx], amount: Number(e.target.value) };
                setTokenItems(next);
              }} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white mb-2" />
              <label className="block text-sm text-purple-200 mb-1">Preço</label>
              <input type="text" value={it.price ?? ''} onChange={(e) => {
                const next = [...tokenItems];
                next[idx] = { ...next[idx], price: e.target.value };
                setTokenItems(next);
              }} className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="text-sm text-purple-200">Tema ativo: {theme?.name || 'Padrão'}</div>
          <button disabled={saving} onClick={saveTokenPackages} className="px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-full font-semibold disabled:opacity-50">
            {saving ? 'Salvando...' : 'Salvar Pacotes'}
          </button>
        </div>
        {message && <div className="mt-2 text-sm text-purple-200">{message}</div>}
      </div>
    </div>
  );
};

export default FinanceDashboard;